import { useEffect, useRef } from "react";

/**
 * Runs `task()` every `intervalMs` milliseconds while the component is mounted
 * AND the browser tab is visible. Automatically pauses when the tab goes to the
 * background and resumes (with an immediate run) when it's visible again.
 *
 * Use this instead of a bare setInterval so users get live data without pressing
 * refresh — but we don't burn bandwidth on hidden tabs.
 *
 * @param {() => (Promise<any> | any)} task
 * @param {number} intervalMs
 * @param {boolean} enabled  — set false to pause the poller (e.g. based on a
 *                             feature flag or when a section isn't active)
 */
export function usePolling(task, intervalMs = 30_000, enabled = true) {
  const taskRef = useRef(task);
  taskRef.current = task;

  useEffect(() => {
    if (!enabled) return;

    let timer = null;
    let cancelled = false;

    const run = async () => {
      if (cancelled) return;
      if (typeof document !== "undefined" && document.hidden) return;
      try {
        await taskRef.current();
      } catch (e) {
        // don't let a transient error stop the poll loop
        // (errors are still logged by the task itself)
      }
    };

    const start = () => {
      if (timer) return;
      timer = setInterval(run, intervalMs);
    };

    const stop = () => {
      if (!timer) return;
      clearInterval(timer);
      timer = null;
    };

    const onVisibility = () => {
      if (document.hidden) {
        stop();
      } else {
        run(); // immediate refresh when user comes back to tab
        start();
      }
    };

    start();
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisibility);
    }

    return () => {
      cancelled = true;
      stop();
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibility);
      }
    };
  }, [intervalMs, enabled]);
}
